<script setup>
/**
 * Flight search for agents.
 *
 * The source badge on every result is not decoration. The same physical flight is
 * routinely offered twice - once as GDS content, once as an NDC offer - at different
 * fares, with different baggage rules and different servicing after sale. An agent who
 * cannot tell them apart can quote one and book the other, so the badge is rendered
 * before the price and is never hidden.
 *
 * Prices arrive as integer minor units plus a decimal place, exactly as Travelport
 * sends them. Formatting happens here, at the last possible moment; nothing upstream
 * turns a fare into a float.
 */
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import TravelSearchAPI from 'dashboard/api/travel/search';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const { t } = useI18n();

const form = ref({
  origin: '',
  destination: '',
  departureDate: '',
  returnDate: '',
  passengers: 1,
});

const offers = ref([]);
const warnings = ref([]);
const contentSources = ref([]);
const isSearching = ref(false);
const hasSearched = ref(false);

const canSearch = computed(
  () =>
    form.value.origin.trim().length === 3 &&
    form.value.destination.trim().length === 3 &&
    !!form.value.departureDate &&
    !isSearching.value
);

const SOURCE_STYLES = {
  gds: 'bg-n-slate-3 text-n-slate-12',
  ndc: 'bg-n-teal-3 text-n-teal-11',
  merged: 'bg-n-amber-3 text-n-amber-11',
};

// Carriers, flight numbers and the brand, joined for display. Built here rather than
// with separators in the template so the whole line is one translatable unit.
const itineraryLabel = offer =>
  [
    ...offer.flights.map(flight => `${flight.carrier}${flight.number}`),
    offer.brand_name,
  ]
    .filter(Boolean)
    .join(' · ');

// Honesty control: preferences can exclude a content source, and an agent reading a
// results list has no way to tell that inventory was withheld.
const excludedSourceNotice = computed(() => {
  if (!contentSources.value.length || contentSources.value.length >= 2)
    return null;
  return t('TRAVEL.FLIGHT_SEARCH.CONTENT_SOURCE_NOTICE', {
    sources: contentSources.value.join(', '),
  });
});

const formatPrice = price => {
  if (!price) return '—';
  const value = price.amount / 10 ** price.decimal_place;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: price.currency,
    minimumFractionDigits: price.decimal_place,
  }).format(value);
};

const search = async () => {
  isSearching.value = true;
  hasSearched.value = true;
  try {
    const { data } = await TravelSearchAPI.search({
      origin: form.value.origin.trim().toUpperCase(),
      destination: form.value.destination.trim().toUpperCase(),
      departure_date: form.value.departureDate,
      return_date: form.value.returnDate || undefined,
      passengers: [{ type: 'ADT', count: form.value.passengers }],
    });
    offers.value = data.offers;
    warnings.value = data.warnings || [];
    contentSources.value = data.content_sources || [];
  } catch (error) {
    offers.value = [];
    warnings.value = [];
    contentSources.value = [];
    useAlert(
      error?.response?.data?.error || t('TRAVEL.FLIGHT_SEARCH.ERROR.GENERIC')
    );
  } finally {
    isSearching.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="w-full max-w-5xl px-6 py-6 mx-auto">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('TRAVEL.FLIGHT_SEARCH.TITLE') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('TRAVEL.FLIGHT_SEARCH.SUBTITLE') }}
      </p>

      <form
        class="grid grid-cols-1 gap-3 p-4 mt-5 border rounded-xl border-n-weak bg-n-solid-1 sm:grid-cols-5"
        @submit.prevent="search"
      >
        <Input
          v-model="form.origin"
          :label="t('TRAVEL.FLIGHT_SEARCH.FORM.ORIGIN')"
          :placeholder="t('TRAVEL.FLIGHT_SEARCH.FORM.ORIGIN_PLACEHOLDER')"
          maxlength="3"
        />
        <Input
          v-model="form.destination"
          :label="t('TRAVEL.FLIGHT_SEARCH.FORM.DESTINATION')"
          :placeholder="t('TRAVEL.FLIGHT_SEARCH.FORM.DESTINATION_PLACEHOLDER')"
          maxlength="3"
        />
        <Input
          v-model="form.departureDate"
          type="date"
          :label="t('TRAVEL.FLIGHT_SEARCH.FORM.DEPART')"
        />
        <Input
          v-model="form.returnDate"
          type="date"
          :label="t('TRAVEL.FLIGHT_SEARCH.FORM.RETURN')"
        />
        <div class="flex items-end">
          <Button
            type="submit"
            :label="t('TRAVEL.FLIGHT_SEARCH.FORM.SEARCH')"
            :is-loading="isSearching"
            :disabled="!canSearch"
            class="w-full"
          />
        </div>
      </form>

      <div
        v-if="warnings.length"
        class="p-3 mt-4 text-sm border rounded-lg border-n-amber-6 bg-n-amber-2 text-n-amber-11"
      >
        <p class="font-medium">
          {{ t('TRAVEL.FLIGHT_SEARCH.PARTIAL_TITLE') }}
        </p>
        <ul class="mt-1 list-disc list-inside">
          <li v-for="(warning, index) in warnings" :key="index">
            {{ warning }}
          </li>
        </ul>
      </div>

      <div
        v-if="excludedSourceNotice"
        class="p-3 mt-4 text-sm border rounded-lg border-n-amber-6 bg-n-amber-2 text-n-amber-11"
      >
        {{ excludedSourceNotice }}
      </div>

      <div v-if="offers.length" class="flex flex-col gap-2 mt-5">
        <div
          v-for="offer in offers"
          :key="`${offer.id}-${offer.sequence}-${offer.brand_name}`"
          class="flex items-center justify-between gap-4 p-4 border rounded-xl border-n-weak bg-n-solid-1"
        >
          <div class="flex flex-col gap-1 min-w-0">
            <div class="flex items-center gap-2">
              <span
                class="px-2 py-0.5 text-xs font-medium uppercase rounded-full"
                :class="SOURCE_STYLES[offer.source]"
              >
                {{
                  t(`TRAVEL.FLIGHT_SEARCH.SOURCE.${offer.source.toUpperCase()}`)
                }}
              </span>
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  t('TRAVEL.FLIGHT_SEARCH.ROUTE', {
                    from: offer.departure,
                    to: offer.arrival,
                  })
                }}
              </span>
            </div>
            <p class="text-sm truncate text-n-slate-11">
              {{ itineraryLabel(offer) }}
            </p>
          </div>
          <div class="text-right shrink-0">
            <p class="text-base font-medium text-n-slate-12">
              {{ formatPrice(offer.total_price) }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{
                t('TRAVEL.FLIGHT_SEARCH.TAXES', {
                  amount: formatPrice(offer.total_taxes),
                })
              }}
            </p>
          </div>
        </div>
      </div>

      <p
        v-else-if="hasSearched && !isSearching"
        class="mt-6 text-sm text-center text-n-slate-11"
      >
        {{ t('TRAVEL.FLIGHT_SEARCH.EMPTY') }}
      </p>

      <!-- Before the first search the page was an empty canvas below the form. -->
      <div
        v-else-if="!isSearching"
        class="flex flex-col items-center gap-2 py-16 text-center"
      >
        <span class="i-ph-airplane-tilt size-7 text-n-slate-9" />
        <p class="max-w-sm text-sm text-n-slate-11">
          {{ t('TRAVEL.FLIGHT_SEARCH.RESTING') }}
        </p>
      </div>
    </div>
  </div>
</template>
